import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useWebSocket from 'common/hooks/useWebSocket';

/**
 * Realtime progress for label regeneration.
 *
 * smartphos-core publishes one `LabelsRegenerated` EventBridge event per label as it commits;
 * perfectrx-websocket-server relays each one to the `bulk_room` socket room as
 * `labels_regenerated`. This hook counts the ones belonging to the request the user just
 * submitted, so the dialog can tick items off as they land instead of showing a single spinner
 * until everything finishes.
 *
 * Event payload:
 *   { requestLineItemIds: string[], completed: number, total: number, requestId: string }
 *
 * Two things worth knowing about the payload:
 *
 * - `completed` / `total` are core's own counters for the whole request. We do NOT trust them
 *   for our progress bar, because core emits for every regeneration — including internal ones
 *   from pharmacy transfer and edit-patient — and those counters belong to whichever request
 *   produced the event. We match on `requestLineItemIds` instead and count what we recognise.
 *
 * - There is no acting-user field, so an event cannot be attributed to a user. That is why this
 *   hook is scoped to "the IDs I just submitted" rather than "events caused by me".
 *
 * A timeout is mandatory, not defensive polish: the socket can be disconnected, the user may be
 * on a screen that never joined the room, or a label may be skipped as ineligible and therefore
 * never reported at all. When it fires, the caller falls back to its previous behaviour.
 */
const DEFAULT_TIMEOUT_MS = 90000;

/**
 * A queued regeneration is not bounded by the request, so the sync timeout does not apply. This
 * covers a large selection working through the queue a batch at a time; if it expires the caller
 * still falls back gracefully, and the LABEL_REGENERATING row tag outlives the dialog either way.
 */
const QUEUED_TIMEOUT_MS = 900000;

/**
 * How long a queued run waits for the server's completion event once every label it submitted
 * has reported in.
 *
 * On the queued branch the server is the authority on the outcome: labels skipped as ineligible
 * never emit, so counting IDs can say "all the ones I heard about are done" while the real
 * result is 197 of 200. Settling on the count alone would also race the server event and
 * announce the run twice. So the count arms this grace instead, and whichever arrives first —
 * the event, or this expiring — settles the run exactly once.
 */
const COMPLETION_GRACE_MS = 30000;

/* Instances of this hook that currently hold a tracked run. Module scope, because the whole point
 * is for one instance to know another is already reporting a run. */
const ACTIVE_TRACKERS = new Set();

/* One completion event, one notification.
 *
 * ACTIVE_TRACKERS answers "is a dialog already reporting this run?" but not "how many IDLE
 * instances are about to announce it?" -- and there is always more than one. Every screen with a
 * PrintLabelProvider mounts an instance (Bulk Labels, Order View, Patient action items, order
 * history, batch shipments), so a completion arriving while none of them is tracking reached each
 * of them in turn and each raised its own toast.
 *
 * Keyed on the payload's IDENTITY rather than a time window: the socket hands the very same
 * object to every handler in one dispatch, so "have I already announced this object?" is exactly
 * the question, with no duration to tune and no risk of swallowing a genuinely separate run that
 * happens to land quickly. A later run arrives as a different object and announces normally. */
let lastAnnouncedCompletion = null;

const claimCompletionAnnouncement = (payload) => {
  if (payload !== null && payload !== undefined && payload === lastAnnouncedCompletion) {
    return false;
  }

  lastAnnouncedCompletion = payload;

  return true;
};

const useLabelRegenerationProgress = ({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  queuedTimeoutMs = QUEUED_TIMEOUT_MS,
  /**
   * Called when a queued run finishes while nothing is being tracked — the user reloaded, or
   * opened a different screen, after sending the work to the queue. There is no dialog left to
   * update, so the caller decides how to tell them.
   */
  onBackgroundComplete,
} = {}) => {
  const { socket, isConnected } = useWebSocket();

  /* Which instances currently hold a tracked run, across the whole app.
   *
   * Every instance decides "is anyone waiting on this run?" from its OWN trackedIdsRef, and each
   * one is a separate closure over separate refs. So a completion event arriving while a dialog
   * was tracking still looked untracked to usePrintLabelContext's instance, which fired its
   * background notification on top of the dialog's own summary -- three toasts for one run
   * (POM-6137 QA). Module-scoped because the instances have no other way to see each other. */
  const instanceKey = useRef({});

  /* Deregister on unmount. Without this a closing dialog would leave its key in the set forever
   * and the guard below would suppress the background notification for the rest of the session --
   * turning a duplicate-toast bug into a missing-toast one. */
  useEffect(() => {
    const key = instanceKey.current;

    return () => {
      ACTIVE_TRACKERS.delete(key);
    };
  }, []);

  const [isTracking, setIsTracking] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);
  const [total, setTotal] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [isQueued, setIsQueued] = useState(false);
  const isQueuedRef = useRef(false);

  // Refs, not state, for everything the socket handler reads: the handler is registered once
  // per socket and must not be torn down and rebuilt on every arriving event.
  const trackedIdsRef = useRef(new Set());
  const completedIdsRef = useRef(new Set());
  const timeoutRef = useRef(null);
  const onCompleteRef = useRef(null);
  const onTimeoutRef = useRef(null);
  const onBackgroundCompleteRef = useRef(onBackgroundComplete);

  /* Multi-run mode (opt-in via startTracking): the tracked ids were submitted as SEVERAL
   * server-side batches, so several `labels_regeneration_completed` events belong to this one
   * wait and the first of them must not settle it. How many to expect is not knowable up front —
   * core only publishes one for a batch that succeeded at something — so the caller derives the
   * count from the override run's outcome and hands it in via setExpectedCompletions, possibly
   * after some completions have already arrived and been accumulated. */
  const isMultiRunRef = useRef(false);
  const expectedCompletionsRef = useRef(null);
  const receivedCompletionsRef = useRef(0);
  const completionTotalsRef = useRef({ total: 0, failed: 0 });

  onBackgroundCompleteRef.current = onBackgroundComplete;

  /**
   * The socket-observed completion count, read from the ref rather than from state.
   *
   * A caller polling on an interval holds the closure it was created with, so `completedCount`
   * would be frozen at whatever it was when the interval was scheduled. This is stable and always
   * current, which is what lets a fallback poll ask "has the socket told us anything yet?".
   */
  const getCompletedCount = useCallback(() => completedIdsRef.current.size, []);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    ACTIVE_TRACKERS.delete(instanceKey.current);
    trackedIdsRef.current = new Set();
    completedIdsRef.current = new Set();
    onCompleteRef.current = null;
    onTimeoutRef.current = null;
    isQueuedRef.current = false;
    isMultiRunRef.current = false;
    expectedCompletionsRef.current = null;
    receivedCompletionsRef.current = 0;
    completionTotalsRef.current = { total: 0, failed: 0 };
    setIsTracking(false);
    setCompletedIds([]);
    setTotal(0);
    setTimedOut(false);
    setIsQueued(false);
  }, [clearTimer]);

  /**
   * Begin tracking a submitted regeneration.
   *
   * Call this immediately BEFORE firing the mutation. Events can arrive while the mutation
   * promise is still open — the first labels commit long before the request returns — so
   * registering afterwards drops the early ones.
   */
  // Arming lives in a ref so the socket handler can extend the window without being rebuilt.
  const armTimeoutRef = useRef(null);

  armTimeoutRef.current = (ms) => {
    clearTimer();

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setTimedOut(true);
      setIsTracking(false);

      const handler = onTimeoutRef.current;
      onTimeoutRef.current = null;
      onCompleteRef.current = null;

      if (handler) {
        handler({
          completed: completedIdsRef.current.size,
          total: trackedIdsRef.current.size,
        });
      }
    }, ms);
  };

  /* Settles a multi-run wait with the accumulated authoritative counts. Lives in a ref (same
   * shape as armTimeoutRef) because two callers need one implementation without rebuilding the
   * socket handler: the completion handler when the expected-th event lands, and
   * setExpectedCompletions when the count arrives after the events already have. */
  const settleMultiRunRef = useRef(null);

  settleMultiRunRef.current = () => {
    clearTimer();
    setIsTracking(false);

    const handler = onCompleteRef.current;
    const { total: reportedTotal, failed } = completionTotalsRef.current;

    onCompleteRef.current = null;
    onTimeoutRef.current = null;

    handler?.({
      completed: reportedTotal - failed,
      total: reportedTotal,
      failed,
    });
  };

  const startTracking = useCallback(
    /**
     * `queued` says the run is ALREADY on the queue — the caller was told so by the mutation it
     * just awaited, rather than by the LabelsRegenerationQueued event.
     *
     * It exists for commitBulkLabelLotNdc, which queues the regeneration inside itself: the
     * queued event is published while that mutation is still open, so a caller that can only
     * start tracking once it knows which rows committed has already missed it. Without this the
     * sync timeout would be armed for work that is going through SQS.
     */
    /**
     * `multiRun` says the ids belong to SEVERAL server-side batches (a client-chunked commit),
     * so completion events are accumulated rather than settling on the first — see the refs
     * above. Callers that submit one batch per commit leave it off and keep today's behaviour.
     */
    ({ requestLineItemIds, onComplete, onTimeout, queued = false, multiRun = false }) => {
      clearTimer();

      trackedIdsRef.current = new Set(requestLineItemIds);

      if (trackedIdsRef.current.size > 0) ACTIVE_TRACKERS.add(instanceKey.current);

      completedIdsRef.current = new Set();
      onCompleteRef.current = onComplete;
      onTimeoutRef.current = onTimeout;
      isMultiRunRef.current = multiRun;
      expectedCompletionsRef.current = null;
      receivedCompletionsRef.current = 0;
      completionTotalsRef.current = { total: 0, failed: 0 };

      setIsTracking(true);
      setCompletedIds([]);
      setTotal(requestLineItemIds.length);
      setTimedOut(false);

      // Joining is idempotent server-side and we deliberately never leave: the Work Queue
      // owns its own membership of this room, and leaving here would silence its
      // bulk_items_printed / bulk_items_filled notifications. The server drops us on
      // disconnect anyway.
      if (socket?.connected) {
        socket.emit('join_bulk_room');
      }

      isQueuedRef.current = queued;
      setIsQueued(queued);

      armTimeoutRef.current(queued ? queuedTimeoutMs : timeoutMs);
    },
    [clearTimer, socket, timeoutMs, queuedTimeoutMs]
  );

  useEffect(() => {
    if (!socket || !isConnected) {
      return undefined;
    }

    const handleLabelsRegenerated = (payload) => {
      // Mirrors useLineItemUpdates: the relay has been seen to deliver either the object or a
      // single-element array, and core nests its own payload under `message`.
      const data = Array.isArray(payload) ? payload[0] : payload;
      const eventData = data?.message || data;
      const ids = eventData?.requestLineItemIds;

      if (!Array.isArray(ids) || ids.length === 0) {
        return;
      }

      if (trackedIdsRef.current.size === 0) {
        return;
      }

      const relevant = ids.filter(
        (id) => trackedIdsRef.current.has(id) && !completedIdsRef.current.has(id)
      );

      if (relevant.length === 0) {
        return;
      }

      relevant.forEach((id) => completedIdsRef.current.add(id));
      setCompletedIds(Array.from(completedIdsRef.current));

      if (completedIdsRef.current.size < trackedIdsRef.current.size) {
        return;
      }

      // Every ID we submitted has reported. On the synchronous branch that is the end of it —
      // nothing else is coming, so settle. On the queued branch the server still owes us an
      // authoritative result, including labels that failed and therefore never emitted, so hold
      // briefly for it rather than settling here and settling again when it lands.
      if (isQueuedRef.current) {
        armTimeoutRef.current(COMPLETION_GRACE_MS);

        return;
      }

      clearTimer();
      setIsTracking(false);

      const handler = onCompleteRef.current;
      onCompleteRef.current = null;
      onTimeoutRef.current = null;

      if (handler) {
        handler({
          completed: completedIdsRef.current.size,
          total: trackedIdsRef.current.size,
        });
      }
    };

    /**
     * Core queued this request instead of running it inside the mutation.
     *
     * Two things change when this arrives. The mutation is about to resolve with `true` while
     * no label has been written yet, so the caller must not read that as completion — that is
     * what `isQueued` is for. And the sync timeout no longer describes the work, so it is
     * re-armed with the queued window.
     */
    const handleLabelsRegenerationQueued = (payload) => {
      const data = Array.isArray(payload) ? payload[0] : payload;
      const eventData = data?.message || data;
      const ids = eventData?.requestLineItemIds;

      if (!Array.isArray(ids) || trackedIdsRef.current.size === 0) {
        return;
      }

      // Same scoping rule as the per-label events: only react to the request we submitted.
      if (!ids.some((id) => trackedIdsRef.current.has(id))) {
        return;
      }

      isQueuedRef.current = true;
      setIsQueued(true);
      armTimeoutRef.current(queuedTimeoutMs);
    };

    /**
     * The server says every batch of a queued run has finished.
     *
     * This is authoritative in a way the per-label count cannot be. Labels skipped as
     * ineligible never emit, so counting IDs alone stalls short of the total and can only end
     * on a timeout — here we learn the real outcome, including how many failed.
     *
     * Scoping is done server-side: the event is delivered to the initiating user's personal
     * room, so anything that arrives here belongs to this user.
     */
    const handleLabelsRegenerationCompleted = (payload) => {
      const data = Array.isArray(payload) ? payload[0] : payload;
      const eventData = data?.message || data;
      const reportedTotal = eventData?.total ?? 0;
      const failed = eventData?.failed ?? 0;

      /* Nothing tracked BY THIS INSTANCE. That only means the run is unattended if no instance
       * anywhere is tracking it -- otherwise a dialog is already reporting the run and this would
       * be a second, redundant notification beside its summary. */
      if (trackedIdsRef.current.size === 0) {
        if (ACTIVE_TRACKERS.size > 0) return;
        if (!claimCompletionAnnouncement(payload)) return;

        onBackgroundCompleteRef.current?.({ total: reportedTotal, failed });

        return;
      }

      /* A multi-run wait spans several server-side batches, and this event is per batch — the
       * first one is not the run. Accumulate the authoritative counts and settle only on the
       * expected-th event; the expected count itself arrives from the override run's outcome via
       * setExpectedCompletions, possibly after some of these have already landed. */
      if (isMultiRunRef.current) {
        /* This event names no batch and no ids, and the same user's OTHER regeneration (a lot/NDC
         * review commit in another dialog) publishes into the same room. The only bound available
         * without a server change: a completion reporting more labels than this run submitted
         * cannot be ours. A foreign run small enough to pass remains indistinguishable — accepted
         * limitation; the count effect and the timeout keep the wait from settling on its say-so
         * alone. */
        if (reportedTotal > trackedIdsRef.current.size) return;

        completionTotalsRef.current = {
          total: completionTotalsRef.current.total + reportedTotal,
          failed: completionTotalsRef.current.failed + failed,
        };
        receivedCompletionsRef.current += 1;

        const expected = expectedCompletionsRef.current;

        if (expected !== null && receivedCompletionsRef.current >= expected) {
          settleMultiRunRef.current();

          return;
        }

        /* Not terminal — sibling batches are still owed. The timer MUST be re-armed rather than
         * cleared or left alone: this event proves the run is alive, and a cleared window with
         * no re-arm would strand the wait with no backstop if a sibling's event never fires.
         * Which window depends on what is still outstanding: once every tracked label has
         * reported, only completion events remain and the short grace covers them — re-arming
         * the full queued window here would turn one missing sibling event into a 15-minute
         * hold the per-label path had already collapsed to 30 seconds. */
        armTimeoutRef.current(
          completedIdsRef.current.size >= trackedIdsRef.current.size
            ? COMPLETION_GRACE_MS
            : queuedTimeoutMs
        );

        return;
      }

      clearTimer();
      setIsTracking(false);

      const handler = onCompleteRef.current;
      onCompleteRef.current = null;
      onTimeoutRef.current = null;

      handler?.({
        completed: reportedTotal - failed,
        total: reportedTotal,
        failed,
      });
    };

    socket.on('labels_regenerated', handleLabelsRegenerated);
    socket.on('labels_regeneration_queued', handleLabelsRegenerationQueued);
    socket.on('labels_regeneration_completed', handleLabelsRegenerationCompleted);

    return () => {
      socket.off('labels_regenerated', handleLabelsRegenerated);
      socket.off('labels_regeneration_queued', handleLabelsRegenerationQueued);
      socket.off('labels_regeneration_completed', handleLabelsRegenerationCompleted);
    };
  }, [socket, isConnected, clearTimer, queuedTimeoutMs]);

  // Never leave a timer behind on unmount.
  useEffect(() => clearTimer, [clearTimer]);

  /**
   * How many completion events the current multi-run wait should expect.
   *
   * Not knowable at startTracking: core publishes a completion only for a batch whose
   * succeeded > 0, so the count comes from the override run's own settled outcome
   * (batchesWithSuccess). Events that arrived before this was called have been accumulated,
   * so reaching the count settles immediately. No-op outside multi-run mode.
   */
  const setExpectedCompletions = useCallback((count) => {
    if (!isMultiRunRef.current) return;

    expectedCompletionsRef.current = count;

    if (onCompleteRef.current && receivedCompletionsRef.current >= count) {
      settleMultiRunRef.current();
    }
  }, []);

  const completedCount = completedIds.length;

  return useMemo(
    () => ({
      startTracking,
      reset,
      getCompletedCount,
      setExpectedCompletions,
      isTracking,
      timedOut,
      isQueued,
      completedCount,
      total,
      completedIds,
      // 0 when nothing is being tracked, so callers can render a determinate bar safely.
      percent: total > 0 ? Math.round((completedCount / total) * 100) : 0,
      isRealtimeAvailable: Boolean(socket && isConnected),
    }),
    [
      startTracking,
      reset,
      getCompletedCount,
      setExpectedCompletions,
      isTracking,
      timedOut,
      isQueued,
      completedCount,
      total,
      completedIds,
      socket,
      isConnected,
    ]
  );
};

export default useLabelRegenerationProgress;
